// InboxCenterView — view unificada Notificações + Mensagens lado a lado.
//
// Por quê: aluno frequentemente entra em "Mensagens" buscando o Chat com
// outros alunos, e vice-versa. Esta view explica a diferença entre os 3
// canais (Notificações, Mensagens administrativas, Chat) e oferece atalho
// rápido pro Chat. As views internas (`NotificationsView` e `MensagensView`)
// permanecem reutilizáveis e renderizam aqui dentro, lado a lado em telas
// largas e empilhadas em mobile.
//
// [[project-peer-chat-autoopen-toggle]] (renomeação pra "Chat" + descoberta)

import { Bell, MessageCircle, MessageSquare } from "lucide-react";
import { NotificationsView } from "./NotificationsView";
import { MensagensView } from "./MensagensView";
import { setActiveNav } from "./App";

export function InboxCenterView() {
  return (
    <div class="space-y-4">
      {/* Cabeçalho explicativo — esclarece a diferença entre os 3 canais
          e dá atalho rápido pro Chat (atrai quem entrou aqui por engano). */}
      <section
        class="rounded-lg border border-border bg-card/60 p-4"
        aria-label="Como funcionam os canais de comunicação"
      >
        <header class="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-base font-bold tracking-tight">
              Caixa de entrada
            </h2>
            <p class="mt-0.5 text-xs text-muted-foreground">
              Aqui você encontra <strong>Notificações</strong> (avisos do sistema)
              e <strong>Mensagens</strong> (recados da equipe administrativa)
              no mesmo lugar. Quer conversar com outro aluno?{" "}
              <button
                type="button"
                onClick={() => setActiveNav("peerchat")}
                class="font-bold text-primary underline-offset-2 hover:underline"
              >
                Vá para o Chat
              </button>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => setActiveNav("peerchat")}
            class="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition-colors hover:bg-primary/10"
            title="Atalho para o Chat com outros alunos"
          >
            <MessageCircle className="size-4" />
            Ir para o Chat
          </button>
        </header>

        <div class="grid gap-2 sm:grid-cols-3">
          <ChannelCard
            icon={Bell}
            label="Notificações"
            desc="Avisos automáticos do sistema: prova publicada, pagamento aprovado, banco gerado pela IA, etc."
            accent="violet"
          />
          <ChannelCard
            icon={MessageSquare}
            label="Mensagens"
            desc="Recados enviados pela equipe administrativa da plataforma ou da sua organização. Você não responde por aqui."
            accent="amber"
          />
          <ChannelCard
            icon={MessageCircle}
            label="Chat"
            desc="Conversa 1:1 com outros alunos das suas conexões aceitas. Mensagens entre vocês, em tempo real."
            accent="primary"
            onClick={() => setActiveNav("peerchat")}
          />
        </div>
      </section>

      {/* Duas colunas com as views internas. Cada uma mantém o próprio
          polling, scroll e estado — não há acoplamento entre elas. */}
      <div class="grid gap-4 lg:grid-cols-2">
        <section
          class="min-w-0 rounded-lg border border-border bg-card/40 p-3"
          aria-label="Notificações"
        >
          <header class="mb-2 flex items-center gap-2 text-sm font-bold">
            <Bell className="size-4 text-violet-600 dark:text-violet-400" />
            Notificações
          </header>
          <NotificationsView compact />
        </section>
        <section
          class="min-w-0 rounded-lg border border-border bg-card/40 p-3"
          aria-label="Mensagens da administração"
        >
          <header class="mb-2 flex items-center gap-2 text-sm font-bold">
            <MessageSquare className="size-4 text-amber-600 dark:text-amber-400" />
            Mensagens da administração
          </header>
          <MensagensView compact />
        </section>
      </div>
    </div>
  );
}

function ChannelCard({
  icon: Icon,
  label,
  desc,
  accent,
  onClick
}: {
  icon: typeof Bell;
  label: string;
  desc: string;
  accent: "violet" | "amber" | "primary";
  onClick?: () => void;
}) {
  const accentClasses =
    accent === "violet"
      ? "border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-300"
      : accent === "amber"
        ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300"
        : "border-primary/40 bg-primary/5 text-primary";
  const baseClass = `flex items-start gap-2 rounded-md border p-3 text-left text-xs ${accentClasses}`;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        class={`${baseClass} transition-colors hover:brightness-110`}
      >
        <Icon className="mt-0.5 size-4 shrink-0" />
        <div class="min-w-0 flex-1">
          <div class="font-bold">{label}</div>
          <div class="mt-0.5 text-[11px] text-muted-foreground">{desc}</div>
        </div>
      </button>
    );
  }
  return (
    <div class={baseClass}>
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div class="min-w-0 flex-1">
        <div class="font-bold">{label}</div>
        <div class="mt-0.5 text-[11px] text-muted-foreground">{desc}</div>
      </div>
    </div>
  );
}
